import React, { useState, useRef } from 'react';
import { API_BASE } from '../apiBase';

type ContentMethod = 'ai-ideation' | 'custom-prompts' | 'topic-based' | 'image-search' | 'blog-processing' | 'event-photo-shoot';

interface GenerationResult {
  success: boolean;
  content?: any;
  images?: string[];
  error?: string;
  message?: string;
  imagePrompts?: Array<{
    prompt: string;
    title?: string;
    productCategories?: string[];
    mood?: string;
    colorPalette?: string;
    products: Array<{
      name: string;
      link: string;
      imageUrl: string;
      description: string;
      score: number;
    }>;
    aiGeneratedImage?: string;
  }>;
}

interface CustomPrompt {
  id: string;
  description: string;
}

const ContentOptionsGenerator: React.FC = () => {
  const [selectedMethod, setSelectedMethod] = useState<ContentMethod>('ai-ideation');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<GenerationResult | null>(null);
  const [progress, setProgress] = useState<string[]>([]);

  // AI Ideation state
  const [currentDate] = useState(new Date().toISOString().split('T')[0]);

  // Custom Prompts state
  const [customPrompts, setCustomPrompts] = useState<CustomPrompt[]>([]);
  const [newPromptDescription, setNewPromptDescription] = useState('');

  // Topic-Based state
  const [topic, setTopic] = useState('');

  // Image Search state
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Blog Processing state
  const [blogContent, setBlogContent] = useState('');
  const [externalLinks, setExternalLinks] = useState('');

  // Event Photo Shoot state
  const [numberOfImages, setNumberOfImages] = useState(3);
  const [eventType, setEventType] = useState('');
  const [eventConcept, setEventConcept] = useState('');
  const [selectedProductsByPrompt, setSelectedProductsByPrompt] = useState<{[key: number]: Set<number>}>({});
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);

  // Helper functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const downloadImage = async (imageUrl: string, filename: string) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  const generateAIImage = async (prompt: string, promptIndex: number) => {
    try {
      const response = await fetch(`${API_BASE}/api/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.imageData) {
        const imageDataUrl = `data:image/png;base64,${data.imageData}`;
        setGeneratedImages(prev => {
          const newImages = [...prev];
          newImages[promptIndex] = imageDataUrl;
          return newImages;
        });
        
        // Update the result to include the generated image
        if (result && result.imagePrompts) {
          setResult(prev => ({
            ...prev!,
            imagePrompts: prev!.imagePrompts!.map((item, idx) => 
              idx === promptIndex ? { ...item, aiGeneratedImage: imageDataUrl } : item
            )
          }));
        }
      }
    } catch (error) {
      console.error('Error generating AI image:', error);
    }
  };

  const downloadAllSelectedImages = () => {
    if (!result?.imagePrompts) return;
    
    result.imagePrompts.forEach((promptData, promptIndex) => {
      const selectedProducts = selectedProductsByPrompt[promptIndex];
      if (selectedProducts && selectedProducts.size > 0) {
        promptData.products.forEach((product, productIndex) => {
          if (selectedProducts.has(productIndex) && product.imageUrl) {
            const filename = `${promptData.title || `prompt-${promptIndex + 1}`}-${product.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.jpg`;
            downloadImage(product.imageUrl, filename);
          }
        });
      }
    });
  };

  const downloadAllAIImages = () => {
    generatedImages.forEach((imageDataUrl, index) => {
      if (imageDataUrl && result?.imagePrompts?.[index]) {
        const filename = `ai-generated-${result.imagePrompts[index].title || `image-${index + 1}`}.png`;
        const link = document.createElement('a');
        link.href = imageDataUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
  };

  const toggleProductSelection = (promptIndex: number, productIndex: number) => {
    setSelectedProductsByPrompt(prev => {
      const newSelection = { ...prev };
      if (!newSelection[promptIndex]) {
        newSelection[promptIndex] = new Set();
      }
      
      if (newSelection[promptIndex].has(productIndex)) {
        newSelection[promptIndex].delete(productIndex);
      } else {
        newSelection[promptIndex].add(productIndex);
      }
      
      return newSelection;
    });
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const addCustomPrompt = () => {
    if (newPromptDescription.trim()) {
      const newPrompt: CustomPrompt = {
        id: Date.now().toString(),
        description: newPromptDescription.trim()
      };
      setCustomPrompts([...customPrompts, newPrompt]);
      setNewPromptDescription('');
    }
  };

  const removeCustomPrompt = (id: string) => {
    setCustomPrompts(customPrompts.filter(p => p.id !== id));
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setResult(null);
    setProgress(['🚀 Starting content generation...']);

    try {
      let requestBody: any = { method: selectedMethod };

      switch (selectedMethod) {
        case 'ai-ideation':
          requestBody.date = currentDate;
          break;
        case 'custom-prompts':
          if (customPrompts.length === 0) {
            throw new Error('Please add at least one custom prompt');
          }
          requestBody.prompts = customPrompts.map(p => p.description);
          break;
        case 'topic-based':
          if (!topic.trim()) {
            throw new Error('Please enter a topic');
          }
          requestBody.topic = topic.trim();
          break;
        case 'image-search':
          if (!uploadedImage) {
            throw new Error('Please upload an image');
          }
          requestBody.image = uploadedImage;
          break;
        case 'blog-processing':
          if (!blogContent.trim()) {
            throw new Error('Please enter blog content');
          }
          requestBody.blogContent = blogContent.trim();
          requestBody.externalLinks = externalLinks.trim();
          break;
        case 'event-photo-shoot':
          if (!eventType.trim()) {
            throw new Error('Please enter an event type');
          }
          if (!eventConcept.trim()) {
            throw new Error('Please enter a concept for the event');
          }
          requestBody.numberOfImages = numberOfImages;
          requestBody.eventType = eventType.trim();
          requestBody.eventConcept = eventConcept.trim();
          break;
      }

      const response = await fetch(`${API_BASE}/api/generate-content-options`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        credentials: 'include', // Include cookies if needed for authentication
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: GenerationResult = await response.json();
      setResult(data);
      
      if (data.success) {
        setProgress(prev => [...prev, '✅ Content generated successfully!']);
      } else {
        setProgress(prev => [...prev, `❌ Error: ${data.error}`]);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setResult({ success: false, error: errorMessage });
      setProgress(prev => [...prev, `❌ Error: ${errorMessage}`]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClear = () => {
    setResult(null);
    setProgress([]);
    setCustomPrompts([]);
    setNewPromptDescription('');
    setTopic('');
    setUploadedImage(null);
    setBlogContent('');
    setExternalLinks('');
    setNumberOfImages(3);
    setEventType('');
    setEventConcept('');
    setSelectedProductsByPrompt({});
    setGeneratedImages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const MethodButton = ({ method, icon, label, description }: { 
    method: ContentMethod, 
    icon: string, 
    label: string, 
    description: string 
  }) => (
    <button
      onClick={() => setSelectedMethod(method)}
      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
        selectedMethod === method
          ? 'border-pink-500 bg-pink-50 shadow-md'
          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl">{icon}</span>
        <div>
          <h3 className="font-semibold text-slate-800">{label}</h3>
          <p className="text-sm text-slate-600 mt-1">{description}</p>
        </div>
      </div>
    </button>
  );

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Controls Panel */}
      <div className="w-full lg:w-1/2 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm">
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Content Creation Options</h2>
            <p className="text-slate-600">Choose your preferred content generation method</p>
          </div>

          {/* Method Selection */}
          <div className="space-y-3">
            <MethodButton
              method="ai-ideation"
              icon="🤖"
              label="AI Ideation Process"
              description="Generate ideas based on current date and trends"
            />
            <MethodButton
              method="custom-prompts"
              icon="✏️"
              label="Custom Text Prompts"
              description="Provide your own scene descriptions"
            />
            <MethodButton
              method="topic-based"
              icon="🏷️"
              label="Topic-Based Post"
              description="Create content about specific topics (rugs, lamps, etc.)"
            />
            <MethodButton
              method="image-search"
              icon="🖼️"
              label="Image-Based Product Search"
              description="Upload a room image for product recommendations"
            />
            <MethodButton
              method="blog-processing"
              icon="📝"
              label="Blog Post Processing"
              description="Analyze blog post, add images and links"
            />
            <MethodButton
              method="event-photo-shoot"
              icon="📷"
              label="Event Photo Shoot"
              description="Generate promotional image prompts for events with product recommendations"
            />
          </div>

          {/* Method-specific inputs */}
          <div className="border-t pt-6">
            {selectedMethod === 'ai-ideation' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800">AI Ideation Settings</h3>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Target Date
                  </label>
                  <input
                    type="date"
                    value={currentDate}
                    disabled
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-600"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    AI will generate home decor ideas relevant to today's date
                  </p>
                </div>
              </div>
            )}

            {selectedMethod === 'custom-prompts' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800">Custom Scene Prompts</h3>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Add Scene Description
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newPromptDescription}
                      onChange={(e) => setNewPromptDescription(e.target.value)}
                      placeholder="e.g., A cozy reading nook with warm lighting"
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                      onKeyPress={(e) => e.key === 'Enter' && addCustomPrompt()}
                    />
                    <button
                      onClick={addCustomPrompt}
                      className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                </div>
                {customPrompts.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Scene Descriptions ({customPrompts.length})</p>
                    {customPrompts.map((prompt, index) => (
                      <div key={prompt.id} className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                        <span className="text-sm text-slate-600 font-medium">Scene {index + 1}:</span>
                        <span className="flex-1 text-sm text-slate-800">{prompt.description}</span>
                        <button
                          onClick={() => removeCustomPrompt(prompt.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedMethod === 'topic-based' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800">Topic-Based Content</h3>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Topic
                  </label>
                  <input
                    type="text"
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder="e.g., rugs, lamps, halloween decor, modern furniture"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Examples: 'rugs', 'lamps', 'halloween', 'christmas', 'modern furniture', 'cozy living room'
                  </p>
                </div>
              </div>
            )}

            {selectedMethod === 'image-search' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800">Image-Based Search</h3>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Upload Room Image
                  </label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                    {uploadedImage ? (
                      <div className="space-y-3">
                        <img 
                          src={uploadedImage} 
                          alt="Uploaded room" 
                          className="max-w-full h-48 object-cover mx-auto rounded-lg"
                        />
                        <button
                          onClick={() => {
                            setUploadedImage(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove Image
                        </button>
                      </div>
                    ) : (
                      <div>
                        <svg className="mx-auto h-12 w-12 text-slate-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                          <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <p className="mt-2 text-sm text-slate-600">Click to upload or drag and drop</p>
                        <p className="text-xs text-slate-400">PNG, JPG, WEBP up to 10MB</p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    {!uploadedImage && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="mt-3 px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                      >
                        Choose Image
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {selectedMethod === 'blog-processing' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800">Blog Post Processing</h3>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Blog Content
                  </label>
                  <textarea
                    value={blogContent}
                    onChange={(e) => setBlogContent(e.target.value)}
                    placeholder="Paste your blog post content here..."
                    className="w-full h-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    External Links (Optional)
                  </label>
                  <textarea
                    value={externalLinks}
                    onChange={(e) => setExternalLinks(e.target.value)}
                    placeholder={`Format: Location::URL
Example:
Introduction paragraph::https://example.com
Conclusion section::https://another-site.com`}
                    className="w-full h-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none text-sm"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Use format: Location::URL (one per line)
                  </p>
                </div>
              </div>
            )}

            {selectedMethod === 'event-photo-shoot' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800">Event Photo Shoot</h3>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Number of Images
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={numberOfImages}
                    onChange={(e) => setNumberOfImages(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Number of promotional images to generate (1-10)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Event Type
                  </label>
                  <input
                    type="text"
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                    placeholder="e.g., Black Friday, Christmas, Valentine's Day, Spring Sale"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    What event are these promotional images for?
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Concept & Theme
                  </label>
                  <textarea
                    value={eventConcept}
                    onChange={(e) => setEventConcept(e.target.value)}
                    placeholder="Describe your concept for the promotional images. Include style, mood, colors, setting, and any specific ideas you want to incorporate..."
                    className="w-full h-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Describe the overall concept, style, and theme for your promotional images
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-6 border-t">
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="flex-1 bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white py-3 px-6 rounded-lg font-semibold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {isGenerating ? 'Generating...' : 'Generate Content'}
            </button>
            <button
              onClick={handleClear}
              disabled={isGenerating}
              className="px-6 py-3 bg-slate-200 text-slate-700 rounded-lg font-semibold transition-colors hover:bg-slate-300 disabled:opacity-50"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Results Panel */}
      <div className="w-full lg:w-1/2 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm">
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-slate-800">Generated Content</h3>
          
          {progress.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-slate-700">Progress</h4>
              <div className="bg-slate-50 rounded-lg p-4 max-h-32 overflow-y-auto">
                {progress.map((step, index) => (
                  <div key={index} className="text-sm text-slate-600 mb-1">
                    {step}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              {result.success ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 font-medium">✅ Content Generated Successfully!</p>
                    {result.message && (
                      <p className="text-green-600 text-sm mt-1">{result.message}</p>
                    )}
                  </div>
                  
                  {result.content && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-slate-700">Generated Content</h4>
                      <div className="bg-slate-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <pre className="text-sm text-slate-700 whitespace-pre-wrap">
                          {typeof result.content === 'string' 
                            ? result.content 
                            : JSON.stringify(result.content, null, 2)
                          }
                        </pre>
                      </div>
                    </div>
                  )}

                  {result.images && result.images.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-slate-700">Generated Images ({result.images.length})</h4>
                      <div className="grid grid-cols-2 gap-3">
                        {result.images.map((imageUrl, index) => (
                          <img
                            key={index}
                            src={imageUrl}
                            alt={`Generated content ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-slate-200"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Event Photo Shoot Results */}
                  {result.imagePrompts && result.imagePrompts.length > 0 && (
                    <div className="space-y-6">
                      {/* Copy All Prompts Button */}
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-slate-700">📷 Event Photo Shoot Prompts ({result.imagePrompts.length})</h4>
                        <div className="flex gap-2">
                          <button
                            onClick={() => copyToClipboard(result.imagePrompts!.map(p => p.prompt).join('\n\n'))}
                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition-colors"
                          >
                            📋 Copy All Prompts
                          </button>
                          <button
                            onClick={downloadAllSelectedImages}
                            className="px-3 py-1 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors"
                          >
                            ⬇️ Download Selected
                          </button>
                          {generatedImages.some(img => img) && (
                            <button
                              onClick={downloadAllAIImages}
                              className="px-3 py-1 bg-purple-500 text-white text-sm rounded-lg hover:bg-purple-600 transition-colors"
                            >
                              🎨 Download AI Images
                            </button>
                          )}
                        </div>
                      </div>

                      {result.imagePrompts.map((promptData, promptIndex) => (
                        <div key={promptIndex} className="border border-slate-200 rounded-xl p-6 bg-white shadow-sm">
                          {/* Prompt Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h5 className="text-lg font-semibold text-slate-800">
                                {promptData.title || `Image ${promptIndex + 1}`}
                              </h5>
                              {promptData.mood && (
                                <p className="text-sm text-slate-600 mt-1">
                                  <span className="font-medium">Mood:</span> {promptData.mood}
                                </p>
                              )}
                              {promptData.colorPalette && (
                                <p className="text-sm text-slate-600">
                                  <span className="font-medium">Colors:</span> {promptData.colorPalette}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => copyToClipboard(promptData.prompt)}
                                className="px-3 py-1 bg-slate-200 text-slate-700 text-sm rounded-lg hover:bg-slate-300 transition-colors"
                              >
                                📋 Copy
                              </button>
                              <button
                                onClick={() => generateAIImage(promptData.prompt, promptIndex)}
                                className="px-3 py-1 bg-pink-500 text-white text-sm rounded-lg hover:bg-pink-600 transition-colors"
                              >
                                🎨 Generate AI Image
                              </button>
                            </div>
                          </div>

                          {/* Image Prompt */}
                          <div className="mb-4">
                            <h6 className="text-sm font-semibold text-slate-700 mb-2">Image Prompt</h6>
                            <div className="bg-slate-50 rounded-lg p-3">
                              <p className="text-sm text-slate-800 whitespace-pre-wrap">{promptData.prompt}</p>
                            </div>
                          </div>

                          {/* AI Generated Image */}
                          {promptData.aiGeneratedImage && (
                            <div className="mb-4">
                              <h6 className="text-sm font-semibold text-slate-700 mb-2">AI Generated Image</h6>
                              <img
                                src={promptData.aiGeneratedImage}
                                alt={`AI generated: ${promptData.title}`}
                                className="w-full max-w-md h-48 object-cover rounded-lg border border-slate-200"
                              />
                            </div>
                          )}

                          {/* Product Recommendations */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h6 className="text-sm font-semibold text-slate-700">
                                🔍 Product Recommendations ({promptData.products.length})
                              </h6>
                              <div className="text-xs text-slate-500">
                                {selectedProductsByPrompt[promptIndex]?.size || 0} selected
                              </div>
                            </div>
                            
                            {promptData.products.length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {promptData.products.map((product, productIndex) => {
                                  const isSelected = selectedProductsByPrompt[promptIndex]?.has(productIndex) || false;
                                  return (
                                    <div
                                      key={productIndex}
                                      onClick={() => toggleProductSelection(promptIndex, productIndex)}
                                      className={`relative cursor-pointer border-2 rounded-lg p-3 transition-all ${
                                        isSelected
                                          ? 'border-pink-500 bg-pink-50 shadow-md'
                                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                                      }`}
                                    >
                                      {isSelected && (
                                        <div className="absolute top-2 right-2 w-5 h-5 bg-pink-500 rounded-full flex items-center justify-center">
                                          <span className="text-white text-xs">✓</span>
                                        </div>
                                      )}
                                      
                                      {product.imageUrl && (
                                        <img
                                          src={product.imageUrl}
                                          alt={product.name}
                                          className="w-full h-24 object-cover rounded-lg mb-2"
                                        />
                                      )}
                                      
                                      <h7 className="text-sm font-semibold text-slate-800 line-clamp-2">
                                        {product.name}
                                      </h7>
                                      
                                      <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                                        {product.description}
                                      </p>
                                      
                                      <div className="flex items-center justify-between mt-2">
                                        <span className="text-xs text-slate-500">
                                          Score: {(product.score * 100).toFixed(1)}%
                                        </span>
                                        {product.link && (
                                          <a
                                            href={product.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-xs text-pink-600 hover:text-pink-700 font-medium"
                                          >
                                            View ↗
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 italic">No product recommendations found for this prompt.</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-medium">❌ Generation Failed</p>
                  {result.error && (
                    <p className="text-red-600 text-sm mt-1">{result.error}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {!result && !isGenerating && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎨</span>
              </div>
              <p className="text-slate-600">Select a content creation method and click "Generate Content" to begin</p>
            </div>
          )}

          {isGenerating && (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-pink-200 border-t-pink-600 rounded-full mx-auto mb-4"></div>
              <p className="text-slate-600">Generating your content...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentOptionsGenerator;
